import { currentUserQuery } from '@/api/user/userQueries';
import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import {
  Card,
  CardContent,
  CardHeader,
  CardsContainer,
} from '@/components/dashboard/DashboardCard';
import { DashboardHero } from '@/components/dashboard/DashboardHero';
import { DashboardTwoColumnSection } from '@/components/dashboard/DashboardTwoColumnSection';
import { UpdateAddressFormDialog } from '@/components/dashboard/account/UpdateAddressFormDialog';
import { UserDataRow } from '@/components/dashboard/account/UserDataRow';
import { Role } from '@/domain/Role';
import { defaultAddressKey } from '@/domain/User';
import { useHasRole } from '@/hooks/useHasRole';
import { UserAvatar as UserAvatarIcon } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';

const invitationDetails = [
  { prop: 'This month', value: 0 },
  { prop: 'Last month', value: 0 },
  { prop: 'All', value: 0 },
] as const;

export const AccountPage = () => {
  const { data: user } = useQuery(currentUserQuery);

  if (!user?.data) {
    return null;
  }

  const address =
    user.data.addresses.length > 0
      ? user.data.addresses.find((addr) => addr.name === defaultAddressKey) ||
        user.data.addresses[0]
      : undefined;
  const { hasRole } = useHasRole(user.data.role);

  const addressString = address
    ? `${address.line1} ${address.city} ${address.country.country} ${address.zipcode}`
    : 'No address set';

  return (
    <DashboardTwoColumnSection>
      <DashboardHero
        className="lg:max-w-100"
        Icon={UserAvatarIcon}
        title="Your account"
        description="View and manage your account details and preferences."
      />

      <CardsContainer className="-mx-4 lg:mx-0 lg:w-full lg:max-w-165">
        <Card>
          <CardHeader>
            <div className="flex w-full flex-col items-start gap-2.5">
              {hasRole(Role.CUSTOMER) && (
                <>
                  <UserDataRow
                    caption="Name"
                    value={`${user.data.name} ${user.data.surname}`}
                  />
                  <UserDataRow
                    caption="Practitioner"
                    value={user.data.practitioners
                      ?.map(
                        (practitioner) =>
                          `${practitioner.name} ${practitioner.surname}`,
                      )
                      .join(', ')}
                  />
                  <UserDataRow
                    caption="Delivery address"
                    value={addressString}
                    noLine
                  />
                </>
              )}

              {hasRole(Role.DOCTOR) && (
                <>
                  <UserDataRow
                    caption="Name"
                    value={`${user.data.name} ${user.data.surname}`}
                  />
                  {/* TODO: <UserDataRow
                    caption="Clinic"
                    value={clinicName}
                    noLine
                  /> */}
                </>
              )}

              {/* Fallback for other roles */}
              {!hasRole(Role.CUSTOMER, Role.DOCTOR) && (
                <UserDataRow
                  caption="Name"
                  value={`${user.data.name} ${user.data.surname}`}
                />
              )}
            </div>
          </CardHeader>

          {hasRole(Role.CUSTOMER) && address && (
            <CardContent>
              <UpdateAddressFormDialog
                address={address}
                trigger={<Button className="w-fit">Change address</Button>}
              />
            </CardContent>
          )}

          {hasRole(Role.DOCTOR) && (
            <CardContent>
              <Heading asChild size="h4" className="mb-2 text-left">
                <p>Invited users</p>
              </Heading>

              <div className="flex justify-between gap-8 pr-8">
                {invitationDetails.map(({ prop, value }) => (
                  <div key={prop}>
                    <p className="mb-2 text-sm">{prop}</p>
                    <p className="text-xl font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      </CardsContainer>
    </DashboardTwoColumnSection>
  );
};
